import { observer } from 'owa-mobx-react';
import { mainModule, main, pageContent as styles_pageContent } from './BookWithMeMainModule.scss';

import React from 'react';
import {
    BookingComposeForm,
    BookingDashboardModule,
    lazyGetPbEnabledStatus,
    type ServicesRequestStatus,
} from 'bookwithme-organizer';
import { getBookWithMeModule } from 'bookwithme-routes';
import { BookingPageContainer, getUrlBookingId } from 'bookwithme-attendee';
import { NotificationBarHost } from 'owa-notification-bar';
import { BookWithMeSuiteHeader } from 'bookwithme-header';
import { ErrorPage, type ErrorPageEntrySource, BookingPageErrorState } from 'bookwithme-shared';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isAnonymousUser } from 'bookings-shared/lib/c2/utils/isAnonymousUser';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { BookingsMetaOSStaticFeedback } from 'bookings-static-feedback';

const SERVICE_STATUS_TO_ERROR: {
    [key in ServicesRequestStatus]?: BookingPageErrorState;
} = {
    [3]: BookingPageErrorState.ErrorAccessDenied,
    [8]: BookingPageErrorState.ErrorPersonalBookingsNotEnabled,
    [6]: BookingPageErrorState.ErrorUnlicensedUser,
    [7]: BookingPageErrorState.ErrorAnonymousSharingDisabled,
};

const BookWithMeMainModule = observer(function BookWithMeMainModule() {
    const currentModule = getBookWithMeModule();
    let isOrganizerView = true;

    // When Personal Bookings is disabled avoid rendering the organizer page, check client flight: cal-personalbookingpage not enabled.
    // Rendering was causing the header and feedback button to show for a few seconds, even when disabled, user could click it to send non actionable feedback.
    const personalBookingsClientEnabled: boolean =
        !isAnonymousUser() && isFeatureEnabled('cal-personalbookingpage');

    // Normal scenarios when need to render the personal bookings page: 1) Personal Bookings is enabled for authenticated user (required to check flight)
    // or 2) is anonymous user to show the sign on prompt.
    const bookingPageStatus: ServicesRequestStatus | undefined =
        personalBookingsClientEnabled || isAnonymousUser()
            ? /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
               * BASELINE. Do not copy and paste!
               *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
              lazyGetPbEnabledStatus.tryImportForRender()?.()
            : 8;
    let showHeader = false;
    /* eslint-disable-next-line react/jsx-no-useless-fragment -- (https://aka.ms/OWALintWiki)
     * Don't use react fragments if there are no children
     *	> Fragments should contain more than one child - otherwise, there’s no need for a Fragment at all. */
    let pageContent: JSX.Element = <></>;
    if (currentModule == 'Organizer' || currentModule == 'Compose') {
        const bookingPageError =
            bookingPageStatus != null ? SERVICE_STATUS_TO_ERROR[bookingPageStatus] : null;
        if (bookingPageError) {
            pageContent = (
                <ErrorPage entrySource={'Organizer'} bookingPageErrorState={bookingPageError} />
            );
        } else {
            let pending = false;
            if (bookingPageStatus == null || bookingPageStatus == 0 || bookingPageStatus == 1) {
                pending = true;
            } else {
                showHeader = true;
            }
            if (currentModule == 'Organizer') {
                pageContent = <BookingDashboardModule pending={pending} />;
            } else if (currentModule == 'Compose') {
                pageContent = <BookingComposeForm pending={pending} />;
            }
        }
    } else if (currentModule == 'Attendee' || currentModule == 'MeetingDetails') {
        const bookingId = currentModule == 'MeetingDetails' ? getUrlBookingId() : undefined;
        pageContent = <BookingPageContainer bookingId={bookingId} />;
        isOrganizerView = false;
        showHeader = true;
    }
    const isMetaOS = isHostAppFeatureEnabled('platformAppSdk');

    return (
        <div className={mainModule}>
            {/* Keep the banner with Feedback button when v is Complete, Error, ... */}
            {currentModule && !isMetaOS && (
                <BookWithMeSuiteHeader
                    isOrganizerView={isOrganizerView}
                    showHeader={!!currentModule && showHeader}
                />
            )}
            <NotificationBarHost hostId={'BookWithMeNotificationBarHost'} />
            <div className={main}>
                <div className={styles_pageContent} data-is-scrollable="true">
                    {/* We need to set data-is-scrollable so the <List> component can properly virtualize pages of dashboard cards*/}
                    {pageContent}
                </div>
            </div>
            {isMetaOS && <BookingsMetaOSStaticFeedback />}
        </div>
    );
}, 'BookWithMeMainModule');

export default BookWithMeMainModule;
